
import { COLD_START_MINS_PER_KM, ROUTING_STORE_CACHE_MAX_AGE_MS } from '../../logistics/constants';
import { DistanceService } from '../../logistics/core/distanceService';
import { calibrateOsrmMinutes } from './osrmCalibration';
import { getStoreCacheWithMeta } from '../../supabaseCache';
import { RoutingOsrmCalibrationConfig } from '../../../types';
import { getOsrmRoute } from '../../routing/osrm';
import { getHereMapsDistance } from '../../../services/hereMaps';

/**
 * v45.2: Routing Engine - Global Leg Intelligence
 * - Implementa cache global de 'pernas' (A para B) para economizar API entre lojas.
 * - Usa precisão de 4 casas decimais para maximizar hit-rate do cache (~11m).
 * v46.0: Added routing-proxy support for secure API key handling
 */

import { getDistanceMatrix, recordRoutingProviderHealth, RoutingProviderId } from '../../../services/maps/distance';
import { getRoutingViaProxy } from '../../../services/supabase';

const isValidCoordinate = (value: number, min: number, max: number): boolean => Number.isFinite(value) && value >= min && value <= max;
const hasValidCoordinates = (lat: number, lng: number): boolean => isValidCoordinate(lat, -90, 90) && isValidCoordinate(lng, -180, 180);
const isFreshStoreCache = (updatedAt: number | null): boolean => {
    if (!Number.isFinite(updatedAt)) return false;
    return Date.now() - (updatedAt as number) <= ROUTING_STORE_CACHE_MAX_AGE_MS;
};

const extractUpdatedAtFromCacheValue = (value: unknown): number | null => {
    if (!value || typeof value !== 'object') return null;
    const candidate = (value as { updatedAt?: unknown }).updatedAt;
    if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;
    if (typeof candidate === 'string') {
        const parsed = Date.parse(candidate);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const trackProviderHealth = (
    provider: RoutingProviderId,
    outcome: 'success' | 'fallback' | 'error',
    details?: { latencyMs?: number; error?: string; fallbackTo?: RoutingProviderId }
): void => {
    void recordRoutingProviderHealth({
        provider,
        outcome,
        latencyMs: details?.latencyMs,
        error: details?.error,
        fallbackTo: details?.fallbackTo
    });
};

/**
 * v46.0: Proxy-based routing via Edge Function
 * Uses secure server-side API keys (never exposed to client)
 * Fallback chain: HERE -> Google -> OSRM -> Heuristic
 */
const getTravelMinutesViaProxy = async (params: {
    originLat: number, originLng: number,
    destLat: number, destLng: number,
    routingApiFallbackEnabled?: boolean,
    routingOsrmCalibration?: RoutingOsrmCalibrationConfig
}): Promise<{ minutes: number, distanceKm: number, source: 'here' | 'google' | 'osrm' | 'heuristic' } | null> => {
    const startedAt = Date.now();
    
    try {
        const result = await getRoutingViaProxy(
            params.originLat,
            params.originLng,
            params.destLat,
            params.destLng,
            {
                transportMode: 'car',
                fallbackEnabled: params.routingApiFallbackEnabled ?? true
            }
        );

        if (!result) {
            console.warn('[Routing] Proxy returned null');
            return null;
        }

        // Track provider health based on the source
        const providerMap: Record<string, RoutingProviderId> = {
            'here': 'here',
            'google': 'google',
            'osrm': 'osrm',
            'heuristic': 'osrm' // Track heuristic as OSRM fallback
        };
        
        const provider = providerMap[result.source] ?? 'osrm';
        trackProviderHealth(provider, 'success', { latencyMs: Date.now() - startedAt });

        // Apply OSRM calibration if needed
        let minutes = result.minutes;
        if (result.source === 'osrm' && params.routingOsrmCalibration) {
            const calibrated = calibrateOsrmMinutes({
                rawDurationMins: result.minutes,
                distanceKm: result.distanceKm,
                calibration: params.routingOsrmCalibration
            });
            minutes = Math.ceil(calibrated);
        }

        return {
            minutes,
            distanceKm: result.distanceKm,
            source: result.source
        };
    } catch (error) {
        console.error('[Routing] Proxy error:', error);
        trackProviderHealth('here', 'error', {
            latencyMs: Date.now() - startedAt,
            error: error instanceof Error ? error.message : 'Unknown error'
        });
        return null;
    }
};

/**
 * v45.2: Routing Engine - Global Leg Intelligence
 * - Implementa cache global de 'pernas' (A para B) para economizar API entre lojas.
 * - Usa precisão de 4 casas decimais para maximizar hit-rate do cache (~11m).
 * v46.0: Added routing-proxy support for secure API key handling
 */

export const getTravelMinutes = async (params: {
    originLat: number, originLng: number,
    destLat: number, destLng: number,
    googleMapsApiKey?: string,
    hereApiKey?: string,
    isEconomicMode?: boolean,
    storeId?: string,
    addressKey?: string,
    orderCreatedAt?: number,
    routingApiProvider?: 'here' | 'google' | 'osrm' | 'auto',
    routingApiFallbackEnabled?: boolean,
    routingOsrmCalibration?: RoutingOsrmCalibrationConfig,
    useProxy?: boolean
}): Promise<{ minutes: number, distanceKm: number, source: 'cache' | 'here' | 'google' | 'osrm' | 'heuristic' | 'legacy_block' }> => {

    if (!hasValidCoordinates(params.originLat, params.originLng) || !hasValidCoordinates(params.destLat, params.destLng)) {
        return { minutes: 0, distanceKm: 0, source: 'heuristic' };
    }

    // v46.0: Use routing-proxy Edge Function for secure API key handling
    if (params.useProxy) {
        const proxyResult = await getTravelMinutesViaProxy(params);
        if (proxyResult) {
            return proxyResult;
        }
        // If proxy fails, fall through to legacy behavior
    }

    const {
        originLat,
        originLng,
        destLat,
        destLng,
        googleMapsApiKey,
        hereApiKey,
        storeId,
        addressKey,
        routingApiProvider = 'osrm',
        routingApiFallbackEnabled = true,
        routingOsrmCalibration
    } = params;
    const provider = routingApiProvider === 'here' ? 'here' :
        routingApiProvider === 'google' ? 'google' : 'osrm';
    const isDev = import.meta.env.DEV;

    // 2. TENTA CACHE DE INTELIGÊNCIA LOCAL (Hub -> Destino)
    if (storeId && addressKey) {
        try {
            const cachedEntry = await getStoreCacheWithMeta<any>(storeId, addressKey, 'route_intelligence');
            const cached = cachedEntry.value;
            const cacheUpdatedAt = extractUpdatedAtFromCacheValue(cached) ?? cachedEntry.updatedAt;

            if (cached && Math.abs(cached.originLat - originLat) < 0.0005 && isFreshStoreCache(cacheUpdatedAt)) {
                return { minutes: cached.baseDurationMins, distanceKm: cached.roadDistanceKm, source: 'cache' };
            }

            if (cached && !isFreshStoreCache(cacheUpdatedAt) && isDev) {
                console.log('[Routing] Ignoring stale store route cache');
            }
        } catch (error) {
            if (isDev) {
                console.warn('[Routing] Cache read failed', error);
            }
        }
    }

    // 3. PRIORITY 1: HERE Maps API (via Edge Function)
    if (provider === 'here' && hereApiKey) {
        const hereStartedAt = Date.now();
        const hereResult = await getHereMapsDistance(originLat, originLng, destLat, destLng, hereApiKey);
        if (hereResult && hereResult.distanceKm > 0) {
            trackProviderHealth('here', 'success', { latencyMs: Date.now() - hereStartedAt });
            return {
                minutes: hereResult.durationMins,
                distanceKm: hereResult.distanceKm,
                source: 'here'
            };
        }

        if (routingApiFallbackEnabled) {
            trackProviderHealth('here', 'fallback', {
                latencyMs: Date.now() - hereStartedAt,
                fallbackTo: googleMapsApiKey ? 'google' : 'osrm',
                error: 'HERE returned empty payload'
            });
        } else {
            trackProviderHealth('here', 'error', {
                latencyMs: Date.now() - hereStartedAt,
                error: 'HERE returned empty payload'
            });
        }

        // Fallback to Google when enabled
        if (routingApiFallbackEnabled && googleMapsApiKey) {
            const googleResult = await getDistanceMatrix(originLat, originLng, destLat, destLng, googleMapsApiKey);
            if (googleResult) {
                if (isDev) console.warn('[Routing] HERE indisponivel, usando Google fallback');
                return { minutes: googleResult.minutes, distanceKm: googleResult.distanceKm, source: 'google' };
            }
        }

        // Fallback to OSRM when enabled
        if (routingApiFallbackEnabled) {
            const osrmStartedAt = Date.now();
            const osrmRoute = await getOsrmRoute(originLat, originLng, destLat, destLng);
            if (osrmRoute && osrmRoute.distanceKm > 0) {
                const calibrated = calibrateOsrmMinutes({
                    rawDurationMins: osrmRoute.durationMins,
                    distanceKm: osrmRoute.distanceKm,
                    calibration: routingOsrmCalibration
                });
                trackProviderHealth('osrm', 'success', { latencyMs: Date.now() - osrmStartedAt });
                if (isDev) console.warn('[Routing] Google indisponivel, usando OSRM fallback');
                return {
                    minutes: Math.ceil(calibrated),
                    distanceKm: osrmRoute.distanceKm,
                    source: 'osrm'
                };
            }
            trackProviderHealth('osrm', 'error', {
                latencyMs: Date.now() - osrmStartedAt,
                error: 'OSRM unavailable after HERE fallback'
            });
        }
    }

    // PRIORITY 2: Google Maps API
    else if (provider === 'google' && googleMapsApiKey) {
        const googleResult = await getDistanceMatrix(originLat, originLng, destLat, destLng, googleMapsApiKey);
        if (googleResult) {
            return { minutes: googleResult.minutes, distanceKm: googleResult.distanceKm, source: 'google' };
        }

        if (!routingApiFallbackEnabled) {
            const roadDist = DistanceService.getDistanceSync(originLat, originLng, destLat, destLng);
            return { minutes: Math.ceil(roadDist * COLD_START_MINS_PER_KM), distanceKm: roadDist, source: 'heuristic' };
        }

        const osrmStartedAt = Date.now();
        const osrmRoute = await getOsrmRoute(originLat, originLng, destLat, destLng);
        if (osrmRoute && osrmRoute.distanceKm > 0) {
            const calibrated = calibrateOsrmMinutes({
                rawDurationMins: osrmRoute.durationMins,
                distanceKm: osrmRoute.distanceKm,
                calibration: routingOsrmCalibration
            });
            trackProviderHealth('osrm', 'success', { latencyMs: Date.now() - osrmStartedAt });
            if (isDev) console.warn('[Routing] Google indisponivel, usando OSRM fallback');
            return {
                minutes: Math.ceil(calibrated),
                distanceKm: osrmRoute.distanceKm,
                source: 'osrm'
            };
        }
        trackProviderHealth('osrm', 'error', {
            latencyMs: Date.now() - osrmStartedAt,
            error: 'OSRM unavailable after Google fallback'
        });
    }

    // PRIORITY 3: OSRM API (default)
    else {
        const osrmStartedAt = Date.now();
        const osrmRoute = await getOsrmRoute(originLat, originLng, destLat, destLng);
        if (osrmRoute && osrmRoute.distanceKm > 0) {
            const calibrated = calibrateOsrmMinutes({
                rawDurationMins: osrmRoute.durationMins,
                distanceKm: osrmRoute.distanceKm,
                calibration: routingOsrmCalibration
            });

            trackProviderHealth('osrm', 'success', { latencyMs: Date.now() - osrmStartedAt });

            return {
                minutes: Math.ceil(calibrated),
                distanceKm: osrmRoute.distanceKm,
                source: 'osrm'
            };
        }

        // Fallback to Google when enabled
        if (routingApiFallbackEnabled && googleMapsApiKey) {
            trackProviderHealth('osrm', 'fallback', {
                latencyMs: Date.now() - osrmStartedAt,
                fallbackTo: 'google',
                error: 'OSRM unavailable'
            });
            const googleResult = await getDistanceMatrix(originLat, originLng, destLat, destLng, googleMapsApiKey);
            if (googleResult) {
                if (isDev) console.warn('[Routing] OSRM indisponivel, usando Google fallback');
                return { minutes: googleResult.minutes, distanceKm: googleResult.distanceKm, source: 'google' };
            }
        } else {
            trackProviderHealth('osrm', 'error', {
                latencyMs: Date.now() - osrmStartedAt,
                error: 'OSRM unavailable and fallback disabled'
            });
        }
    }

    // 5. Fallback heuristico extremo para indisponibilidade total de APIs.
    const roadDist = DistanceService.getDistanceSync(originLat, originLng, destLat, destLng);
    return {
        minutes: Math.ceil(roadDist * COLD_START_MINS_PER_KM), // ~21km/h média urbana
        distanceKm: roadDist,
        source: 'heuristic'
    };
};

// calculateLinearDistance removido em favor do DistanceService centralizado.
